// API helpers for the Collings-Data scraped property database integration.
// Uses the authenticated, cookie-based `api` client (this CRM uses HttpOnly
// cookies — NOT localStorage bearer tokens).

import api from "./index";

const BASE = "public-search/collings-data";

// --- Property profiles: one permanent profile per property, every source on it ---
// Christian, Monday 12573204628 (26 Oct 2026). Tabs are the status (all, buy,
// rent, sold, off_market), never the source. Same params as below.
export const searchPropertyProfiles = (params = {}) =>
  api.get("/api/suburb-intelligence/property-profiles/", { params });

export const getPropertyProfile = (id) =>
  api.get(`/api/suburb-intelligence/property-profiles/${id}/`);

// The same property's private layer, Collings staff only: owners on file, CRM
// listing, Database rows (Christian, Monday 13354971743, 27 Oct 2026).
export const getPropertyProfilePrivate = (id) =>
  api.get(`/api/suburb-intelligence/property-profiles/${id}/private/`);

// The profile behind an old per-source link: { homer: id } | { collings: id } | { owner: key }.
export const resolvePropertyProfile = (params) =>
  api.get("/api/suburb-intelligence/property-profiles/resolve/", { params });

// --- Public Property Search (Homer's sale and rental campaigns, synced nightly) ---
// Christian, Monday 12573204628 (12 Oct 2026). Params: q, tab, surrounding,
// property_type, price_min/max, beds_min/max, baths_min, cars_min,
// land_min/max, sort, page, page_size.
export const searchPublicProperties = (params = {}) =>
  api.get("/api/suburb-intelligence/public-property-search/", { params });

export const getPublicPropertyListing = (id) =>
  api.get(`/api/suburb-intelligence/public-property-search/${id}/`);

// One property from our own scraped data, with its Homer campaigns (Monday 12573204628).
export const getScrapedProperty = (id) =>
  api.get(`/api/suburb-intelligence/public-property-search/collings/${id}/`);

// One address from the office's spreadsheets, Homer listing or not (Monday 13354971743).
export const getOwnerRecord = (key) =>
  api.get(`/api/suburb-intelligence/public-property-search/owner/${encodeURIComponent(key)}/`);

// --- Live browse (proxy over the ~1.7M scraped properties) ---
export const searchCollingsData = (params = {}) =>
  api.get("public-search/collings-data-properties/", { params });

// Recently-SOLD listings for the "Sold" tab. Reuses the SAME source the Buyer
// Portal's "Sold Properties" page uses — Collings' own sold listings — so the
// data matches across products. Returns the standard paginated envelope
// { count, next, previous, results }.
export const searchPortalSold = (params = {}) =>
  api.get("api/buyer-portal/properties/", {
    params: { portal_category: "sold", ...params },
  });

// Off-market listings for the "Off Market" tab. The scraped upstream only carries
// CURRENT listings (no off-market), so these come from the CRM/Buyer Portal — the
// same source & envelope as Sold, filtered by portal_category=off_market.
export const searchPortalOffMarket = (params = {}) =>
  api.get("api/buyer-portal/properties/", {
    params: { portal_category: "off_market", ...params },
  });

export const getCollingsDataStats = () => api.get(`${BASE}/stats/`);

export const collingsDataAutocomplete = (q, limit = 10) =>
  api.get(`${BASE}/autocomplete/`, { params: { q, limit } });

// --- Save-on-demand profiles ---
export const addCollingsPropertyToCRM = (property) =>
  api.post(`${BASE}/add/`, property);

export const getSavedCollingsProperties = (params = {}) =>
  api.get(`${BASE}/saved/`, { params });

export const getSavedCollingsProperty = (id) =>
  api.get(`${BASE}/saved/${id}/`);

export const deleteSavedCollingsProperty = (id) =>
  api.delete(`${BASE}/saved/${id}/`);

// --- Owners ---
export const getCollingsPropertyOwners = (propertyId) =>
  api.get(`${BASE}/saved/${propertyId}/owners/`);

export const addCollingsPropertyOwner = (propertyId, ownerData) =>
  api.post(`${BASE}/saved/${propertyId}/owners/`, ownerData);

export const updateCollingsPropertyOwner = (propertyId, ownerId, ownerData) =>
  api.patch(`${BASE}/saved/${propertyId}/owners/${ownerId}/`, ownerData);

export const removeCollingsPropertyOwner = (propertyId, ownerId) =>
  api.delete(`${BASE}/saved/${propertyId}/owners/${ownerId}/`);
